/**
 * PDE-2621 — PUWYLO sunset config (Config Hub sourcing for the sunset windows).
 *
 * Resolves the tunable sunset windows from the Config Hub block
 * `Sephora.configurationSettings.puwyloSunsetConfig`, falling back per-field to
 * DEFAULT_SUNSET_CONFIG when a value is missing or non-numeric. Read at call-time so
 * late-bound runtime config is honored and so it stays SSR-safe (`Sephora` may be undefined
 * server-side and the block may be populated after this module is imported).
 *
 * Field names mirror iOS (PDE-2624) parity:
 *   - clickHideAfterHours   (24) -> hide a SKU for N hours after it is clicked
 *   - noClickHideAfterDays   (5) -> hide a never-clicked SKU once it is N days old
 *   - suppressionWindowDays (15) -> total per-SKU suppression window before the SKU is fresh again
 */

import Storage from 'utils/localStorage/Storage';

const { HOURS, DAYS } = Storage;

/**
 * Config Hub fallbacks. Keep in sync with the `puwyloSunsetConfig` block served by
 * configurationSettings.
 * @type {{ clickHideAfterHours: number, noClickHideAfterDays: number, suppressionWindowDays: number }}
 */
const DEFAULT_SUNSET_CONFIG = {
    clickHideAfterHours: 24,
    noClickHideAfterDays: 5,
    suppressionWindowDays: 15
};

/**
 * Coerce a config value to a finite number. Config Hub serves the windows as strings
 * (e.g. `"24"`), so numeric strings are parsed; empty/whitespace strings and anything that does not
 * resolve to a finite number (NaN, null, undefined, booleans, non-numeric strings) fall back to the
 * provided default.
 * @param {*} value
 * @param {number} fallback
 * @returns {number}
 */
const toNum = (value, fallback) => {
    const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;

    return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Resolve the numeric sunset config, applying per-field defaults for any missing/invalid value.
 * @returns {{ clickHideAfterHours: number, noClickHideAfterDays: number, suppressionWindowDays: number }}
 */
const getSunsetConfig = () => {
    const cfg = (typeof Sephora !== 'undefined' && Sephora.configurationSettings?.puwyloSunsetConfig) || null;

    return {
        clickHideAfterHours: toNum(cfg?.clickHideAfterHours, DEFAULT_SUNSET_CONFIG.clickHideAfterHours),
        noClickHideAfterDays: toNum(cfg?.noClickHideAfterDays, DEFAULT_SUNSET_CONFIG.noClickHideAfterDays),
        suppressionWindowDays: toNum(cfg?.suppressionWindowDays, DEFAULT_SUNSET_CONFIG.suppressionWindowDays)
    };
};

/**
 * Resolve the config into concrete millisecond windows used by the sunset state machine.
 * @returns {{ clickWindow: number, noClickWindow: number, suppressionWindow: number }}
 */
const getWindows = () => {
    const { clickHideAfterHours, noClickHideAfterDays, suppressionWindowDays } = getSunsetConfig();

    return {
        clickWindow: clickHideAfterHours * HOURS,
        noClickWindow: noClickHideAfterDays * DAYS,
        suppressionWindow: suppressionWindowDays * DAYS
    };
};

export {
    DEFAULT_SUNSET_CONFIG, getSunsetConfig, getWindows
};
